import { useQuery } from '@tanstack/react-query';
import { homepageApiService, HomepageData, HomepageDataParams } from '../../services/homepageApiService';
import { cachingStrategyService, DataType } from '../../services/cachingStrategyService';
import { debugError, debugLog } from '../../utils/debugLog';

export const HOMEPAGE_DATA_QUERY_KEY = ['homepageData'] as const;

export const useHomepageDataQuery = (params: HomepageDataParams = {}) => {
  // Get optimized caching strategy for homepage data
  const cacheStrategy = cachingStrategyService.getStrategy(DataType.HOMEPAGE_DATA);
  
  return useQuery({
    queryKey: [...HOMEPAGE_DATA_QUERY_KEY, params],
    queryFn: async (): Promise<HomepageData> => {
      try {
        const response = await homepageApiService.getHomepageData(params);
        debugLog('🔍 Hook DEBUG: Raw API response:', response);
        
        if (!response.success) {
          // Create a more descriptive error for better user experience
          const error = new Error(response.error || 'Failed to fetch homepage data');
          error.name = 'HomepageDataError';
          throw error;
        }
        
        if (!response.data) {
          // Handle case where success is true but data is missing
          const error = new Error('Homepage data is missing from successful response');
          error.name = 'HomepageDataError';
          throw error;
        }
        
        debugLog('🔍 Hook DEBUG: Extracted data:', response.data);
        return response.data;
      } catch (error) {
        // Log error for debugging but re-throw for React Query to handle
        debugError('Homepage data query failed:', error);
        throw error;
      }
    },
    // Apply centralized caching strategy
    staleTime: cacheStrategy.staleTime,
    gcTime: cacheStrategy.gcTime,
    refetchInterval: cacheStrategy.refetchInterval,
    refetchIntervalInBackground: cacheStrategy.refetchIntervalInBackground,
    refetchOnWindowFocus: cacheStrategy.refetchOnWindowFocus,
    refetchOnReconnect: cacheStrategy.refetchOnReconnect,
    retry: cacheStrategy.retry,
    retryDelay: cacheStrategy.retryDelay,
  });
};
